import React from "react";
import clsx from "clsx";

const Table = ({ children, className = "", ...props }) => {
  return (
    <div className="overflow-x-auto rounded-lg border border-dark-200 dark:border-dark-700">
      <table
        className={clsx("min-w-full divide-y divide-dark-200 dark:divide-dark-700", className)}
        {...props}
      >
        {children}
      </table>
    </div>
  );
};

Table.Head = ({ children, className = "" }) => (
  <thead className={clsx("bg-dark-50 dark:bg-dark-900", className)}>
    {children}
  </thead>
);

Table.Body = ({ children, className = "" }) => (
  <tbody
    className={clsx(
      "divide-y divide-dark-200 dark:divide-dark-700 bg-white dark:bg-dark-800",
      className
    )}
  >
    {children}
  </tbody>
);

Table.Row = ({ children, className = "", hover = true, selected = false, onClick }) => (
  <tr
    onClick={onClick}
    className={clsx(
      hover && "hover:bg-dark-50 dark:hover:bg-dark-700/50",
      selected && "bg-primary-50 dark:bg-primary-900/20",
      onClick && "cursor-pointer",
      className
    )}
  >
    {children}
  </tr>
);

Table.HeaderCell = ({
  children,
  className = "",
  align = "left",
  sortable = false,
  sorted = null,
  onSort,
  width,
}) => {
  const alignClasses = {
    left: "text-left",
    center: "text-center",
    right: "text-right",
  };

  return (
    <th
      scope="col"
      style={width ? { width } : undefined}
      className={clsx(
        "px-4 py-3 text-xs font-semibold uppercase tracking-wider text-dark-600 dark:text-dark-400",
        alignClasses[align],
        sortable && "cursor-pointer select-none hover:text-dark-900 dark:hover:text-dark-200",
        className
      )}
      onClick={sortable && onSort ? onSort : undefined}
    >
      <div className={clsx("flex items-center gap-1", align === "right" && "justify-end", align === "center" && "justify-center")}>
        {children}
        {sortable && (
          <span className="ml-1">
            {sorted === "asc" && (
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
              </svg>
            )}
            {sorted === "desc" && (
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            )}
            {sorted === null && (
              <svg className="w-4 h-4 opacity-40" fill="currentColor" viewBox="0 0 20 20">
                <path d="M5 9l5-5 5 5H5zm0 2l5 5 5-5H5z" />
              </svg>
            )}
          </span>
        )}
      </div>
    </th>
  );
};

Table.Cell = ({ children, className = "", align = "left", wrap = false }) => {
  const alignClasses = {
    left: "text-left",
    center: "text-center",
    right: "text-right",
  };

  return (
    <td
      className={clsx(
        "px-4 py-3 text-sm text-dark-700 dark:text-dark-300",
        !wrap && "whitespace-nowrap",
        alignClasses[align],
        className
      )}
    >
      {children}
    </td>
  );
};

// Empty state for tables
Table.Empty = ({ message = "Nenhum dado encontrado", icon, action }) => (
  <tr>
    <td colSpan="100%" className="px-4 py-12 text-center">
      <div className="flex flex-col items-center">
        {icon || (
          <svg
            className="w-12 h-12 text-dark-300 dark:text-dark-600 mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
            />
          </svg>
        )}
        <p className="text-dark-500 dark:text-dark-400 text-sm">{message}</p>
        {action && <div className="mt-4">{action}</div>}
      </div>
    </td>
  </tr>
);

// Loading state for tables
Table.Loading = ({ columns = 5, rows = 5 }) => (
  <>
    {Array.from({ length: rows }).map((_, rowIndex) => (
      <tr key={rowIndex}>
        {Array.from({ length: columns }).map((_, colIndex) => (
          <td key={colIndex} className="px-4 py-3">
            <div className="h-4 bg-dark-200 dark:bg-dark-700 rounded animate-pulse" />
          </td>
        ))}
      </tr>
    ))}
  </>
);

export default Table;
